import React, { useState } from 'react';
import './LoginPage.css';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../services/api';

type ChallengeName = 'SOFTWARE_TOKEN_MFA' | 'NEW_PASSWORD_REQUIRED';

interface Challenge {
    name: ChallengeName;
    session: string;
}


interface UserProfile {
    username: string,
    groups: string[],
    attributes: any
}

interface AuthTokens {
    idToken: string;
    refreshToken: string;
    expireIn: number;
    userProfile: UserProfile;
}

type ViewMode = 'login' | 'forgotPassword' | 'resetPassword';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [challenge, setChallenge] = useState<Challenge | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('login');
    const [resetCode, setResetCode] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const { login } = useAuth();

    const handleSuccessfulLogin = (tokens: AuthTokens) => {
        console.log('Login successful');
        login(tokens);
    };


    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setError(null);

        if (viewMode === 'forgotPassword') {
            await handleForgotPasswordSubmit();
            return;
        }

        if (viewMode === 'resetPassword') {
            await handleResetPasswordSubmit();
            return;
        }

        // If there's a challenge, we call the challenge handler instead.
        if (challenge) {
            await handleChallengeSubmit();
            return;
        }

        try {
            const response = await apiFetch('/api/pub/auth', {
                method: 'POST',
                body: JSON.stringify({ username, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                // API might return a challenge even on a non-200 response
                if (data.challengeName && data.session) {
                    setChallenge({ name: data.challengeName, session: data.session });
                } else {
                    throw new Error(data.error || 'An unknown error occurred.');
                }
            } else if (data.challengeName && data.session) {
                // Handle challenge responses
                setChallenge({ name: data.challengeName, session: data.session });
            } else if (data.idToken) {
                handleSuccessfulLogin(data);
            }

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Login failed. Please try again.';
            setError(errorMessage);
            console.error('Login error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChallengeSubmit = async () => {
        if (!challenge) return;

        const payload: { [key: string]: any } = {
            username,
            session: challenge.session,
            challengeName: challenge.name,
        };

        if (challenge.name === 'SOFTWARE_TOKEN_MFA') {
            payload.mfaCode = mfaCode;
        } else if (challenge.name === 'NEW_PASSWORD_REQUIRED') {
            payload.newPassword = newPassword;
        }

        try {
            const response = await apiFetch('/api/pub/auth/challenge', {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Challenge submission failed.');
            }

            if (data.idToken) {
                handleSuccessfulLogin(data as AuthTokens);
            } else {
                // This case might occur if another challenge is issued, though unlikely.
                setError('An unexpected response was received after submitting the challenge.');
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred. Please try again.';
            setError(errorMessage);
            // On challenge failure, you might want to reset to the initial login screen
            setChallenge(null);
            setPassword('');
            setNewPassword('');
            setMfaCode('');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPasswordSubmit = async () => {
        try {
            const response = await apiFetch('/api/pub/auth/forgot-password', {
                method: 'POST',
                body: JSON.stringify({ username }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to send reset code.');
            }
            // On success, move to the next step
            setViewMode('resetPassword');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPasswordSubmit = async () => {
        try {
            const response = await apiFetch('/api/pub/auth/reset-password', {
                method: 'POST',
                body: JSON.stringify({
                    username,
                    code: resetCode,
                    newPassword: newPassword,
                }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to reset password.');
            }
            // On success, show a success message and return to login
            alert('Password has been successfully reset. Please log in with your new password.');
            setViewMode('login');
            resetFormState();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const resetFormState = () => {
        setUsername('');
        setPassword('');
        setError(null);
        setChallenge(null);
        setMfaCode('');
        setNewPassword('');
        setResetCode('');
    };

    const handleBackToLogin = () => {
        setViewMode('login');
        resetFormState();
    };

    const renderChallengeForm = () => {
        if (!challenge) return null;

        if (challenge.name === 'SOFTWARE_TOKEN_MFA') {
            return (
                <div className="form-group">
                    <label htmlFor="mfaCode">MFA Code</label>
                    <input
                        id="mfaCode"
                        type="text"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        required
                        autoFocus
                        disabled={isLoading}
                    />
                </div>
            );
        }

        if (challenge.name === 'NEW_PASSWORD_REQUIRED') {
            return (
                <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <input
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        autoFocus
                        disabled={isLoading}
                    />
                </div>
            );
        }

        return null;
    };

    const renderForgotPasswordForm = () => (
        <>
            <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoFocus
                    disabled={isLoading}
                />
            </div>
        </>
    );

    const renderResetPasswordForm = () => (
        <>
            <div className="form-group">
                <label htmlFor="username">Username</label>
                <input id="username" type="text" value={username} required disabled />
            </div>
            <div className="form-group">
                <label htmlFor="resetCode">Reset Code</label>
                <input
                    id="resetCode"
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required
                    autoFocus
                    disabled={isLoading}
                />
            </div>
            <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={isLoading}
                />
            </div>
        </>
    );

    const renderMainLoginForm = () => (
        <>
            <div className="form-group">
                <label htmlFor="username">Username</label>
                <input id="username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} required disabled={isLoading} />
            </div>
            <div className="form-group">
                <label htmlFor="password">Password</label>
                <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={isLoading} />
            </div>
        </>
    );

    return (
        <div className="login-container">
            <form onSubmit={handleSubmit} className="login-form">
                <div className="login-header">
                    <img src="/logo192.png" alt="RapID Logo" className="login-logo" aria-label="RapID Logo" />
                    <p className="warning-label">This system is for authorized law enforcement use only. Unauthorized use is prohibited and may result in criminal prosecution, civil penalties, or other disciplinary action.</p>
                </div>
                <h2>
                    {viewMode === 'login' && (challenge ? 'Complete Sign-In' : 'Login')}
                    {viewMode === 'forgotPassword' && 'Forgot Password'}
                    {viewMode === 'resetPassword' && 'Reset Your Password'}
                </h2>
                {error && <p className="error-message">{error}</p>}

                {viewMode === 'login' && (challenge ? renderChallengeForm() : renderMainLoginForm())}
                {viewMode === 'forgotPassword' && renderForgotPasswordForm()}
                {viewMode === 'resetPassword' && renderResetPasswordForm()}

                <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Processing...' : (viewMode === 'login' ? (challenge ? 'Submit' : 'Login') : (viewMode === 'forgotPassword' ? 'Send Code' : 'Reset Password'))}
                </button>

                {viewMode === 'login' && !challenge && (
                    <div className="form-footer">
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a href="#" onClick={() => setViewMode('forgotPassword')}>Forgot Password?</a>
                    </div>
                )}

                {viewMode !== 'login' && (
                    <div className="form-footer">
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a href="#" onClick={handleBackToLogin}>Back to Login</a>
                    </div>
                )}
            </form>
        </div>
    );
};

export default LoginPage;
